<template>
  <div class="signin-page">
    <div class="signin-page__glow signin-page__glow--one"></div>
    <div class="signin-page__glow signin-page__glow--two"></div>

    <div class="signin-shell">
      <section class="signin-hero">
        <div class="signin-brand">
          <img src="/logo.png" alt="Dayruns" class="signin-brand__logo" />
          <div>
            <p class="signin-brand__eyebrow">Dayruns Admin</p>
            <h1>Access your dashboard with ease.</h1>
          </div>
        </div>

        <p class="signin-hero__copy">
          Sign in to continue to your admin workspace.
        </p>

        <div class="signin-points">
          <article class="signin-point">
            <span class="signin-point__icon">01</span>
            <div>
              <h2>Fast access</h2>
              <p>Return to your dashboard in seconds.</p>
            </div>
          </article>
          <article class="signin-point">
            <span class="signin-point__icon">02</span>
            <div>
              <h2>Secure workspace</h2>
              <p>Keep your account protected at all times.</p>
            </div>
          </article>
          <article class="signin-point">
            <span class="signin-point__icon">03</span>
            <div>
              <h2>Professional control</h2>
              <p>Handle key admin tasks from one place.</p>
            </div>
          </article>
        </div>
      </section>

      <section class="signin-card">
        <div class="signin-card__header">
          <p class="signin-card__eyebrow">Welcome back</p>
          <h2>Sign in to continue</h2>
          <p>Use your email and password.</p>
        </div>

        <form class="signin-form" @submit.prevent="login">
          <div class="signin-field">
            <label for="email">Email address</label>
            <div class="signin-field__control">
              <span class="signin-field__icon">@</span>
              <input
                id="email"
                type="email"
                autocomplete="email"
                placeholder="Enter email address"
                v-model.trim="username"
              />
            </div>
          </div>

          <div class="signin-field">
            <label for="password">Password</label>
            <div class="signin-field__control">
              <span class="signin-field__icon signin-field__icon--lock">PW</span>
              <input
                id="password"
                :type="passwordFieldType"
                autocomplete="current-password"
                placeholder="Enter password"
                v-model="password"
              />
              <button
                class="signin-field__toggle"
                type="button"
                @click="showPassword = !showPassword"
              >
                {{ showPassword ? 'Hide' : 'Show' }}
              </button>
            </div>
          </div>

          <div class="signin-actions">
            <a class="signin-actions__link" href="/forgot-password">Forgot password?</a>
            <a class="signin-actions__link" href="https://dayruns.com" target="_blank" rel="noreferrer">
              Visit website
            </a>
          </div>

          <button
            id="submit4"
            class="signin-submit"
            :disabled="isSubmitButtonDisabled || loading"
            type="submit"
            v-if="!loading"
          >
            Sign in
          </button>

          <button class="signin-submit" type="button" disabled v-if="loading">
            <span class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
            Signing in...
          </button>
        </form>
      </section>
    </div>
  </div>
</template>

<script>
import swal from 'sweetalert2';

export default {
  name: 'SigninPage',

  data() {
    return {
      appTitle: 'Dayruns',
      username: '',
      password: '',
      loading: false,
      showPassword: false,
    };
  },

  beforeMount() {
    document.title = this.appTitle;
  },

  computed: {
    isSubmitButtonDisabled() {
      return this.username === '' || this.password === '';
    },

    passwordFieldType() {
      return this.showPassword ? 'text' : 'password';
    },
  },

  methods: {
    async login() {
      try {
        this.loading = true;
        const response = await this.$http.post('/login', {
          email: this.username,
          password: this.password,
        });

        this.loading = false;

        if (!response.data.success) {
          swal.fire({
            icon: 'error',
            title: 'Sign in failed',
            text: response.data.message,
          });
          return;
        }

        const token = response.data.token;
        const role = response.data.data[0].role;
        const username = response.data.data[0].username;
        const userid = response.data.data[0].id;
        const email = response.data.email;

        localStorage.setItem('wertyuiojhdfghhdrtfyguh', token);
        localStorage.setItem('role', role);
        localStorage.setItem('username', username);
        localStorage.setItem('userid', userid);
        localStorage.setItem('email', email);

        this.$router.push('/home');
      } catch (err) {
        this.loading = false;
        swal.fire({
          icon: 'error',
          title: 'Unable to sign in',
          text: err.response ? err.response.data.message : err.message,
        });
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import url('https://fonts.googleapis.com/css2?family=Poppins:wght@300;400;500;600;700;800&display=swap');

.signin-page {
  position: relative;
  min-height: 100vh;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 32px;
  color: #e5e7eb;
  background:
    linear-gradient(135deg, rgba(13, 17, 26, 0.96), rgba(28, 34, 48, 0.92)),
    radial-gradient(circle at top right, rgba(211, 62, 116, 0.18), transparent 30%);
}

.signin-page__glow {
  position: absolute;
  border-radius: 999px;
  filter: blur(36px);
  opacity: 0.7;
  pointer-events: none;
}

.signin-page__glow--one {
  width: 280px;
  height: 280px;
  top: -80px;
  right: -60px;
  background: rgba(211, 62, 116, 0.26);
}

.signin-page__glow--two {
  width: 340px;
  height: 340px;
  bottom: -120px;
  left: -80px;
  background: rgba(59, 130, 246, 0.18);
}

.signin-shell {
  position: relative;
  z-index: 1;
  width: min(920px, 100%);
  display: grid;
  grid-template-columns: 1fr 0.8fr;
  gap: 16px;
  align-items: stretch;
}

.signin-hero,
.signin-card {
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 28px;
  backdrop-filter: blur(20px);
  box-shadow: 0 28px 80px rgba(0, 0, 0, 0.35);
}

.signin-hero {
  padding: 28px;
  background:
    linear-gradient(180deg, rgba(255, 255, 255, 0.08), rgba(255, 255, 255, 0.03)),
    rgba(8, 12, 20, 0.5);
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.signin-brand {
  display: flex;
  align-items: center;
  gap: 18px;
}

.signin-brand__logo {
  width: 56px;
  height: 56px;
  object-fit: contain;
  border-radius: 20px;
  background: rgba(255, 255, 255, 0.92);
  padding: 10px;
  box-shadow: 0 10px 28px rgba(0, 0, 0, 0.18);
}

.signin-brand__eyebrow,
.signin-card__eyebrow {
  margin: 0 0 8px;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  font-size: 12px;
  font-weight: 700;
  color: #f8a6bf;
}

.signin-brand h1 {
  margin: 0;
  font-size: clamp(1.55rem, 2.7vw, 2.25rem);
  line-height: 1.05;
  color: #fff;
  max-width: 11ch;
}

.signin-hero__copy {
  margin: 14px 0 0;
  max-width: 42ch;
  color: rgba(226, 232, 240, 0.84);
  font-size: 0.9rem;
  line-height: 1.55;
}

.signin-points {
  display: grid;
  gap: 10px;
  margin-top: 18px;
}

.signin-point {
  display: grid;
  grid-template-columns: auto 1fr;
  gap: 10px;
  align-items: start;
  padding: 12px 14px;
  border-radius: 14px;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.07);
}

.signin-point__icon {
  width: 32px;
  height: 32px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 999px;
  background: rgba(211, 62, 116, 0.18);
  color: #ffd6e3;
  font-weight: 700;
  font-size: 0.8rem;
}

.signin-point h2 {
  margin: 0 0 4px;
  font-size: 0.9rem;
  color: #fff;
}

.signin-point p {
  margin: 0;
  color: rgba(226, 232, 240, 0.78);
  line-height: 1.4;
  font-size: 0.84rem;
}

.signin-card {
  padding: 24px;
  background: rgba(255, 255, 255, 0.96);
  color: #111827;
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.signin-card__header h2 {
  margin: 0;
  font-size: clamp(1.25rem, 1.8vw, 1.65rem);
  color: #0f172a;
}

.signin-card__header p {
  margin: 10px 0 0;
  color: #64748b;
  line-height: 1.6;
}

.signin-form {
  margin-top: 18px;
}

.signin-field {
  margin-bottom: 12px;
}

.signin-field label {
  display: inline-block;
  margin-bottom: 7px;
  font-size: 0.82rem;
  font-weight: 600;
  color: #334155;
}

.signin-field__control {
  display: flex;
  align-items: center;
  gap: 9px;
  min-height: 46px;
  padding: 0 13px;
  border-radius: 13px;
  border: 1px solid #dbe4ef;
  background: #f8fafc;
  transition:
    border-color 0.2s ease,
    box-shadow 0.2s ease,
    transform 0.2s ease;
}

.signin-field__control:focus-within {
  border-color: #d33e74;
  box-shadow: 0 0 0 4px rgba(211, 62, 116, 0.12);
  transform: translateY(-1px);
}

.signin-field__icon {
  width: 22px;
  height: 22px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 999px;
  background: rgba(211, 62, 116, 0.12);
  color: #d33e74;
  font-weight: 700;
  flex: 0 0 auto;
}

.signin-field__icon--lock {
  font-size: 1.2rem;
}

.signin-field__control input {
  width: 100%;
  border: 0;
  outline: 0;
  background: transparent;
  font-size: 0.9rem;
  color: #0f172a;
}

.signin-field__control input::placeholder {
  color: #94a3b8;
}

.signin-field__toggle {
  border: 0;
  background: transparent;
  color: #d33e74;
  font-weight: 600;
  font-size: 0.8rem;
  padding: 0;
  flex: 0 0 auto;
}

.signin-actions {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin: 12px 0 16px;
}

.signin-actions__link {
  color: #64748b;
  text-decoration: none;
  font-size: 0.82rem;
  font-weight: 500;
}

.signin-actions__link:hover {
  color: #d33e74;
}

.signin-submit {
  width: 100%;
  min-height: 46px;
  border: 0;
  border-radius: 13px;
  background: linear-gradient(135deg, #d33e74, #b02f60);
  color: #fff;
  font-weight: 700;
  font-size: 0.86rem;
  letter-spacing: 0.02em;
  text-transform: uppercase;
  box-shadow: 0 16px 30px rgba(211, 62, 116, 0.28);
}

.signin-submit:disabled {
  opacity: 0.72;
  cursor: not-allowed;
  box-shadow: none;
}

@media (max-width: 980px) {
  .signin-shell {
    grid-template-columns: 1fr;
  }

  .signin-hero {
    padding: 22px;
  }

  .signin-card {
    padding: 20px;
  }
}

@media (max-width: 640px) {
  .signin-page {
    padding: 18px;
  }

  .signin-hero,
  .signin-card {
    border-radius: 22px;
  }

  .signin-brand {
    align-items: flex-start;
  }

  .signin-brand__logo {
    width: 48px;
    height: 48px;
  }

  .signin-brand h1 {
    font-size: 1.45rem;
  }

  .signin-actions {
    flex-direction: column;
    align-items: flex-start;
  }
}
</style>
